import { get, set, clone } from 'lodash';
import {
    SET_MEDIA,
    NEXT_PAGE,
    CLEAN_UP_BOOK,
    SET_TRC_CONTENT,
    RESET_ACTIVE_PAGE,
    RESET_PAGES_VIEWED,
    SET_COMPREHENSION_ATTEMPT,
    INCREASE_COMPREHENSION_ATTEMPT,
    SET_JPLAYER_OPTION,
    SET_TRC_BOOK_RESULT,
    COMPLETE_STEP_INDEX,
    SET_READING_COMPLETE,
    SET_LAST_RECORDED_PAGE,
    SET_CURRENT_STEP_INDEX,
    SET_PROGRESS_STEPS_COUNT,
    UPDATE_SELECTED_COMPREHENSION_RESPONSES,
    SET_COMPREHENSION_COMPLETE,
    SET_PASSED_MIC_READ_CHECK,
    SET_ATTEMPTED_MIC_READ_CHECK,
    SET_PASSED_MULTIPLE_CHOICE_READ_CHECK,
    SET_ATTEMPTED_MULTIPLE_CHOICE_READ_CHECK,
    SET_DID_PREVIOUSLY_ENABLE_MIC,
} from './trcActions';
import bookReducer from './bookReducer';
import { SET_RESULT } from '../common/api/resultActions';
import { TRO } from '../constants/measure';
import { TRO_PASSING_LEVEL_STATUSES } from '../constants/book';
import { VR_RESULT_STATUS } from '../constants/error';
import { isAddBookResultRequest, isAudioSavingRequest } from '../common/api/apiUtils';
import { API_CALL_SUCCEEDED, API_CALL_FAILED, START_API_CALL } from '../common/api/apiCallActions';
import {
    getVrStatus,
    getBookResults,
    getActiveBookResults,
    getVrErrorReason,
    getLatestPassingLevel,
    getSetMediaDemoVideoProgress,
    getSetJPlayerOptionsDemoVideoProgress,
    getVrScore,
} from './troUtils';
import { VERIFICATION_RESULT, VERIFICATION_STATUS } from './pages/instructions/TROInstructionsConstants';
import { SET_VERIFICATION_RESULT, SET_VERIFICATION_STATUS, EXECUTE_API_CALL_SUCCEEDED_EFFECT } from '../common/api/trcoActions';
import { TRO_DEMO_VIDEO_PLAYER_ID } from '../common/components/demoVideos/demoVideoConstants';

export const defaultState = {
    isDataFetched: false,
    book: bookReducer(undefined, {}),
    selectedResponses: {},
    comprehensionAttempts: {},
    bookResults: [],
    isReadingComplete: false,
    isVrResultProcessingTimeout: false,
    activeVideoId: null,
    demoVideosProgress: {},
    verificationStatus: VERIFICATION_STATUS.IDLE,
    verificationResult: VERIFICATION_RESULT.UNAVAILABLE,
    onboardingProgressBar: {
        stepsCount: 0,
        currentStepIndex: 0,
        completedStepIndex: -1
    },
    didApiCallSucceed: false,
    didApiCallFail: false,
    prevApiRequest: {},
    readingCheck: {
        passedMicRead: false,
        didAttemptMicReadingCheck: false,
        passedMultipleChoiceRead: false,
        didAttemptMultiChoiceReadingCheck: false,
    },
    didPreviouslyEnableMic: false,
};

const trcReducer = (state = defaultState, action) => {
    switch (action.type) {
    case SET_TRC_CONTENT:
        return {
            ...state,
            isDataFetched: true,
            book: bookReducer(state.book, action),
            bookResults: getBookResults(state.bookResults, action.content.reading_level, action.content.book_id),
        };
    case SET_RESULT: {
        if (action.result.measure !== TRO.MEASURE) {
            return state;
        }
        const activeBookResults = getActiveBookResults(action.result);
        if (activeBookResults.length === 0) return state;

        return {
            ...state,
            bookResults: activeBookResults,
        };
    }
    case NEXT_PAGE:
    case RESET_ACTIVE_PAGE:
    case RESET_PAGES_VIEWED:
    case SET_LAST_RECORDED_PAGE:
        return {
            ...state,
            book: bookReducer(state.book, action),
        };
    case CLEAN_UP_BOOK:
        return {
            ...state,
            isDataFetched: false,
            selectedResponses: {},
            book: bookReducer(state.book, action),
            isReadingComplete: defaultState.isReadingComplete,
        };
    case UPDATE_SELECTED_COMPREHENSION_RESPONSES:
        return {
            ...state,
            selectedResponses: {
                ...state.selectedResponses,
                [action.questionIndex]: action.studentResponse,
            },
        };

    case SET_TRC_BOOK_RESULT: {
        const { bookResult } = action;
        const hasMultipleResults = state.bookResults.length > 1;
        return {
            ...state,
            bookResults: state.bookResults.map((result, i) => {
                if (hasMultipleResults && i !== 1) {
                    return state.bookResults[0];
                }
                return {
                    passingLevel: getLatestPassingLevel(bookResult),
                    readingLevel: bookResult.book_reading_level,
                    bookId: bookResult.book_id,
                    bookTitle: bookResult.book_title,
                    genre: bookResult.book_genre,
                    relativeLevel: bookResult.relative_level,
                    vrStatus: getVrStatus(bookResult),
                    vrScore: getVrScore(bookResult),
                    vrErrorReason: getVrErrorReason(bookResult),
                    comprehensionScore: bookResult.comprehension_result.score,
                    isBookPassed: bookResult.passing_level === TRO_PASSING_LEVEL_STATUSES.PASS,
                    responsesQuestions: bookResult.comprehension_result.item_level_details.items,
                    isComprehensionComplete: get(result, 'comprehension_result.score') !== null
                };
            })
        };
    }
    case API_CALL_SUCCEEDED:
        return {
            ...state,
            didApiCallSucceed: true,
            prevApiRequest: {
                endpoint: action.endpoint,
                requestConfig: action.requestConfig
            }
        };
    case START_API_CALL:
        return {
            ...state,
            didApiCallSucceed: defaultState.didApiCallSucceed,
            didApiCallFail: defaultState.didApiCallFail
        };
    case API_CALL_FAILED:
        return {
            ...state,
            didApiCallFail: true,
            prevApiRequest: {
                endpoint: action.endpoint,
                requestConfig: action.requestConfig
            }
        };
    case EXECUTE_API_CALL_SUCCEEDED_EFFECT:
        if (isAddBookResultRequest(action.payload.prevApiRequest)) {
            const { payload: { prevApiRequest } } = action;
            const requestBookId = JSON.parse(prevApiRequest.requestConfig.body).data.book_id;
            return {
                ...state,
                bookResults: state.bookResults.map((result) => {
                    if (result.bookId === requestBookId && result.passingLevel === TRO_PASSING_LEVEL_STATUSES.NOT_STARTED) {
                        return {
                            ...result,
                            passingLevel: TRO_PASSING_LEVEL_STATUSES.IN_PROGRESS,
                        };
                    }
                    return result;
                })
            };
        }
        if (isAudioSavingRequest(action.payload.prevApiRequest)) {
            // TODO: consider introducing requests meta info instead of parsing body
            const { payload: { bookId } } = action;
            return {
                ...state,
                bookResults: state.bookResults.map(result => {
                    if (result.bookId === bookId && result.vrStatus === VR_RESULT_STATUS.UNAVAILABLE) {
                        return {
                            ...result,
                            vrStatus: VR_RESULT_STATUS.PENDING,
                        };
                    }
                    return result;
                })
            };
        }
        return state;
    case SET_COMPREHENSION_COMPLETE:
        return {
            ...state,
            bookResults: state.bookResults.map(result => {
                if (result.bookId === action.bookId && !result.isComprehensionComplete) {
                    return {
                        ...result,
                        isComprehensionComplete: true,
                    };
                }
                return result;
            })
        };
    case SET_READING_COMPLETE:
        return {
            ...state,
            isReadingComplete: true,
        };
    case SET_MEDIA:
        if (action.id === TRO_DEMO_VIDEO_PLAYER_ID) {
            return {
                ...state,
                activeVideoId: action.media.demoVideoId,
                demoVideosProgress: getSetMediaDemoVideoProgress(state, action),
            };
        }
        return state;
    case SET_JPLAYER_OPTION:
        if (action.id === TRO_DEMO_VIDEO_PLAYER_ID) {
            return {
                ...state,
                demoVideosProgress: getSetJPlayerOptionsDemoVideoProgress(state, action),
            };
        }
        return state;
    case SET_VERIFICATION_RESULT:
        if (action.payload.verification_passed) {
            return {
                ...state,
                verificationResult: VERIFICATION_RESULT.PASSED,
            };
        }
        if (action.payload.reset_verification) {
            return {
                ...state,
                verificationResult: defaultState.verificationResult,
            };
        }
        return {
            ...state,
            verificationResult: VERIFICATION_RESULT.FAILED,
        };
    case SET_VERIFICATION_STATUS:
        return {
            ...state,
            verificationStatus: action.payload,
        };
    case SET_CURRENT_STEP_INDEX:
        return {
            ...state,
            onboardingProgressBar: set(clone(state.onboardingProgressBar), 'currentStepIndex', action.payload),
        };
    case COMPLETE_STEP_INDEX:
        return {
            ...state,
            onboardingProgressBar: set(clone(state.onboardingProgressBar), 'completedStepIndex', action.payload),
        };
    case SET_PROGRESS_STEPS_COUNT:
        return {
            ...state,
            onboardingProgressBar: set(clone(state.onboardingProgressBar), 'stepsCount', action.payload),
        };
    case SET_PASSED_MIC_READ_CHECK:
        if (action.payload.verification_passed) {
            return {
                ...state,
                readingCheck: set(clone(state.readingCheck), 'passedMicRead', true),
            };
        }
        return {
            ...state,
            readingCheck: set(clone(state.readingCheck), 'passedMicRead', false),
        };
    case SET_ATTEMPTED_MIC_READ_CHECK:
        return {
            ...state,
            readingCheck: set(clone(state.readingCheck), 'didAttemptMicReadingCheck', true),
        };
    case SET_PASSED_MULTIPLE_CHOICE_READ_CHECK:
        return {
            ...state,
            readingCheck: set(clone(state.readingCheck), 'passedMultipleChoiceRead', action.payload.isPass),
        };
    case SET_ATTEMPTED_MULTIPLE_CHOICE_READ_CHECK:
        return {
            ...state,
            readingCheck: set(clone(state.readingCheck), 'didAttemptMultiChoiceReadingCheck', true),
        };
    case SET_COMPREHENSION_ATTEMPT: {
        const { comprehensionAttempts } = state;
        const { questionIndex } = action;
        const isComprehensionSet = comprehensionAttempts[questionIndex];
        if (isComprehensionSet) {
            return state;
        }
        return {
            ...state,
            comprehensionAttempts: {
                ...comprehensionAttempts,
                [questionIndex]: 1,
            },
        };
    }
    case INCREASE_COMPREHENSION_ATTEMPT: {
        const { comprehensionAttempts } = state;
        const { questionIndex } = action;
        return {
            ...state,
            comprehensionAttempts: {
                ...comprehensionAttempts,
                [questionIndex]: comprehensionAttempts[questionIndex] + 1 || 1,
            }
        };
    }
    case SET_DID_PREVIOUSLY_ENABLE_MIC: {
        return {
            ...state,
            didPreviouslyEnableMic: true,
        };
    }
    default:
        return state;
    }
};

export default trcReducer;
